import './DashboardGrid.css';

import Widget from './widgets/Widget';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { generateClassName } from '../../b2b-common/helpers';
import { useEffect, useState } from 'react';
import { isEqual } from 'lodash';

const ResponsiveGridLayout = WidthProvider(Responsive);

const calculateLayout = (widgets) => {
    return widgets.map((widget, index) => {
        const { row, column, width, height } = widget.widgetConfig;
        const minHeight = widget.widgetConfig.extraFields?.minHeight ?? 1;
        return {
            i: widget.widgetId.toString(),
            x: column - 1,
            y: row - 1,
            w: width,
            h: height,
            minH: minHeight || 1,
        };
    });
};

const calculateOccupiedCells = (layout) => {
    const occupiedCells = new Set();
    layout.forEach(item => {
        for (let i = 0; i < item.w; i++) {
            for (let j = 0; j < item.h; j++) {
                occupiedCells.add(`${item.x + i}-${item.y + j}`);
            }
        }
    });
    return occupiedCells;
};

const calculateAvailableCells = (rows, columns, occupiedCells) => {
    const availableCells = [];
    for (let y = 0; y < rows; y++) {
        for (let x = 0; x < columns; x++) {
            if (!occupiedCells.has(`${x}-${y}`)) {
                availableCells.push({ x, y });
            }
        }
    }
    return availableCells;
};

const DashboardGrid = ({ rows = 3, columns = 3, 
widgets = [], rowHeight = 210, onWidgetDeleted, onEditWidget,
    horizontalGap = 10, verticalGap = 10,
    horizontalPadding = 0, verticalPadding = 0,
    className = null, isPublicDashboard = false, password = null }) => {
    
    const [currentWidgets, setCurrentWidgets] = useState(widgets);
    const [currentLayout, setCurrentLayout] = useState(calculateLayout(widgets));
    const [currentOccupiedCells, setCurrentOccupiedCells] = useState(calculateOccupiedCells(currentLayout));
    const [currentAvailableCells, setCurrentAvailableCells] = useState(calculateAvailableCells(rows, columns, currentOccupiedCells));

    useEffect(() => {
        if (!isEqual(currentWidgets, widgets)) {
            setCurrentWidgets(widgets);
            setCurrentLayout(calculateLayout(widgets));
            setCurrentOccupiedCells(calculateOccupiedCells(calculateLayout(widgets)));
            setCurrentAvailableCells(calculateAvailableCells(rows, columns, calculateOccupiedCells(calculateLayout(widgets))));
        }
    }, [widgets, rows, columns]);

    if (!currentWidgets || currentWidgets.length === 0) {
        return null;
    }

    const handleDeleteWidget = async (widgetId) => {
        if (onWidgetDeleted) {
            onWidgetDeleted(widgetId);
        }
    }

    const handleEditWidget = (widget) => {
        if (onEditWidget) {
            onEditWidget(widget);
        }
    }

    return (
        <ResponsiveGridLayout
            className={generateClassName('dashboard-grid','',className)}
            layouts={{ lg: currentLayout }}
            // breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: columns, md: columns, sm: columns, xs: columns, xxs: columns }}
            rowHeight={rowHeight} // Each row is this minimum in height
            isResizable={false}
            isDraggable={false}
            autoSize={true}
            margin={[horizontalGap, verticalGap]}
            containerPadding={[horizontalPadding, verticalPadding]}
        >
            {currentWidgets.map((widget, index) => (
                <div key={widget.widgetId}
                    id={`grid-item-${widget.widgetId}`}
                    data-grid={currentLayout[index]}
                    className={generateClassName('grid','-item',className)}>
                    <Widget widget={widget} onDelete={handleDeleteWidget} onEdit={handleEditWidget} atPublicDashboard={isPublicDashboard} password={password} />
                </div>
            ))}
            {currentAvailableCells.map((cell, index) => (
                <div key={`available-${index}`} data-grid={{ x: cell.x, y: cell.y, w: 1, h: 1 }} className="grid-item empty" />
            ))}
        </ResponsiveGridLayout>
    );
};

export default DashboardGrid;